import { CircularProgress, ProgressFilter } from "@/components/ProgressChips";
import AssessmentsDropdown, { AssessmentStatuses } from "./AssessmentsDropdown";
import { Lesson } from "@/types/tutor";
import DetailsPopover from "@/components/workspace/DetailsPopover";
import PrimaryButton from "@/components/PrimaryButton";
import SecondaryButton from "@/components/SecondaryButton";
import api, { tutorApi } from "@/utils/axios-utils";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { ChevronRight, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/useAuthStore";
import type { EntryIntent } from "@/features/tutor/vo-demo/contracts/resumeContracts";
import { useDebugStore } from "@/store/useDebugStore";
import { withAsOf } from "@/utils/as-of";
import {
  clearLessonStartupPerfAnchor,
  getLessonStartupPerfNow,
  recordLessonStartupPerf,
  resolveLessonStartupPerfVariant,
  startLessonStartupPerfAnchor,
} from "@/features/tutor/utils/lessonStartupPerf";

const STATUS_COLORS = {
  MASTERED: "#96D932",
  IN_PROGRESS: "#FAD30D",
  NOT_STARTED: "#E3E3E3",
};

type LessonWithStatus = Lesson & {
  status?: ProgressFilter;
  goalsMet?: number;
  totalGoals?: number;
  assignedAt?: string;
  // The date the lesson is *scheduled for*, which is not the date the row was
  // written. Sequenced rows are all created in one activation, so `assignedAt`
  // is the same day for every lesson in the schedule and says nothing about
  // when any of them is due.
  scheduledFor?: string | null;
  // No date of its own: No expectation mode's locked Upcoming, not reached
  // yet, so neither an assignment date nor the authoring date stands in.
  undated?: boolean;
  finishedAt?: number | null;
  startedAt?: number | null;
  classroomId?: number | null;
};

const Spinner = ({ className = "w-4 h-4 mr-2" }: { className?: string }) => (
  <svg
    className={`animate-spin ${className}`}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
    />
  </svg>
);

const ActionButton = ({
  status,
  canStart = true,
  // "Pending" is the export's word for a blocked action. The default used to be
  // "Coming soon", which in the export names a different thing entirely -- the
  // placeholder shown when no next lesson is scheduled -- so a caller that
  // omitted this prop claimed nothing was scheduled.
  unavailableLabel = "Pending",
  unavailableReason,
  disabled,
  loading = false,
  onClick,
}: {
  status?: ProgressFilter;
  canStart?: boolean;
  unavailableLabel?: string;
  unavailableReason?: string;
  disabled?: boolean;
  loading?: boolean;
  onClick: () => Promise<void>;
}) => {
  const label = !canStart
    ? unavailableLabel
    : status === "in_progress"
      ? loading
        ? "Resuming…"
        : "Resume Lesson"
      : status === "mastered"
        ? loading
          ? "Starting…"
          : "Learn Again"
        : loading
          ? "Starting…"
          : "Start Lesson";
  const Button =
    canStart && status === "in_progress" ? PrimaryButton : SecondaryButton;
  return (
    <Button
      type="button"
      className={
        canStart && status === "in_progress"
          ? "et-primary-button"
          : "et-secondary-button"
      }
      disabled={!canStart || disabled}
      title={unavailableReason}
      aria-busy={loading}
      onClick={onClick}
    >
      {loading && <Spinner />}
      <span aria-live="polite">{label}</span>
      {canStart && !loading && <ChevronRight size={18} aria-hidden="true" />}
      {!canStart && unavailableReason && (
        <span className="sr-only">: {unavailableReason}</span>
      )}
    </Button>
  );
};

const LessonCard = ({
  lesson,
  classroomId,
  globalLoadingLessonId,
  onStartStateChange,
  assessmentStatuses,
  canStart = true,
  unavailableLabel,
  unavailableReason,
  availability,
  sequenceLabel,
  position,
  locked = false,
  fromTab,
}: {
  lesson: LessonWithStatus;
  classroomId: string;
  globalLoadingLessonId?: number | null;
  onStartStateChange?: (loading: boolean, lessonId: number) => void;
  assessmentStatuses?: AssessmentStatuses;
  canStart?: boolean;
  unavailableLabel?: string;
  unavailableReason?: string;
  /**
   * Renders the export's Available/Pending pill under the title, and greys the
   * card when unavailable. Opt-in, because the export only draws this on its
   * upcoming cards — passing it for a finished lesson would label it
   * "Available".
   */
  availability?: {
    label: string;
    available: boolean;
    /** The export draws a started lesson amber, between available and
     *  pending -- it can be resumed, but it is not the fresh one. */
    tone?: "available" | "started" | "pending";
  };
  /**
   * Shows the export's "Lesson N of M" chip on a
   * weekly card, where position in the week is what the student needs and the
   * assigned date is the same for the whole batch.
   */
  sequenceLabel?: string;
  /**
   * No expectation mode's Upcoming pill, in place of the progress status: the
   * lesson has not been reached, so where it sits is what the student needs
   * (Alloy, student v7). `next` marks the one the pointer reaches next.
   */
  position?: { label: string; next: boolean };
  /**
   * Not startable and nothing to open: a padlock and "Locked" stand where the
   * action and Details would be (Alloy, student v7's Upcoming card).
   */
  locked?: boolean;
  /**
   * No expectation mode's Incomplete or Finished tab, passed on to the tutor:
   * a lesson behind Today never moves the student, and only where it was
   * opened from says so -- once finished, a Today lesson sits in Finished too.
   * From Finished, the tutor also says practising it keeps their place (Alloy,
   * student v7).
   */
  fromTab?: "incomplete" | "finished";
}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const userStartupPerfVariant = useAuthStore(
    (state) => state.user?.config?.lessonStartupPerfVariant,
  );
  const lessonStartupPerfOverride = useDebugStore(
    (state) => state.lessonStartupPerfOverride,
  );
  const lessonStartupPerfVariant = useMemo(
    () =>
      resolveLessonStartupPerfVariant(
        userStartupPerfVariant,
        lessonStartupPerfOverride,
      ),
    [lessonStartupPerfOverride, userStartupPerfVariant],
  );
  const useOptimizedStartup = lessonStartupPerfVariant === "optimized";

  const startLesson = async (l: Lesson) => {
    setIsLoading(true);
    onStartStateChange?.(true, l.id);
    // Typed because it lands in an untyped analytics payload, which would
    // otherwise swallow a value getLessonEntryContext cannot produce.
    const entryIntent: EntryIntent =
      lesson.status === "mastered"
        ? "learn_again"
        : lesson.status === "in_progress"
          ? "resume"
          : "default";

    startLessonStartupPerfAnchor({
      lesson_id: l.id,
      entry_intent: entryIntent,
      variant: lessonStartupPerfVariant,
    });

    if (!useOptimizedStartup) {
      const preflightStartedAt = getLessonStartupPerfNow();
      recordLessonStartupPerf("dashboard_preflight_begin", {
        lesson_id: l.id,
        variant: lessonStartupPerfVariant,
      });
      try {
        await tutorApi.post("/session/lesson_discovery", {
          lesson_id: l.id,
          ...(classroomId && { classroom_id: Number(classroomId) }),
        });
        recordLessonStartupPerf(
          "dashboard_preflight_success",
          {
            lesson_id: l.id,
            variant: lessonStartupPerfVariant,
          },
          preflightStartedAt,
        );
      } catch (error: any) {
        recordLessonStartupPerf(
          "dashboard_preflight_error",
          {
            lesson_id: l.id,
            variant: lessonStartupPerfVariant,
            status: error.response?.status,
            message: error?.message,
          },
          preflightStartedAt,
        );
        if (error.response?.status === 403) {
          alert(
            error.response?.data?.detail ||
              "Your access to tutoring sessions has been suspended. Please contact your teacher.",
          );
          setIsLoading(false);
          onStartStateChange?.(false, l.id);
          clearLessonStartupPerfAnchor();
          return;
        }
        console.error("Error checking session:", error);
      }
    }

    // Through `withAsOf`, because this builds the next URL from scratch. QA's
    // date simulation rides the address bar, so any page that rewrites its own
    // query string drops it -- and starting a lesson is the one navigation a
    // tester makes from a simulated day, which put them back on the real date
    // mid-run. Draft lessons never reach here: `assignable_lesson_clause`
    // already keeps unpublished content out of the schedule and the picker.
    const params: Record<string, string> = {};
    if (classroomId) {
      params.classroomId = classroomId;
      params.lessonName = l.name;
    }
    if (entryIntent === "learn_again" || entryIntent === "resume") {
      params.entryIntent = entryIntent;
    }
    if (fromTab) params.fromTab = fromTab;
    navigate(
      `/vo-demo/${l.id}?${new URLSearchParams(withAsOf(params)).toString()}`,
    );
    setIsLoading(false);
    onStartStateChange?.(false, l.id);
  };

  const borderColor =
    lesson.status === "mastered"
      ? STATUS_COLORS.MASTERED
      : lesson.status === "in_progress"
        ? STATUS_COLORS.IN_PROGRESS
        : STATUS_COLORS.NOT_STARTED;

  const goalsMet = Number(lesson.goalsMet ?? 0);
  const totalGoals = Number(lesson.totalGoals ?? 0);
  // Prefer the scheduled date. It is what the schedule tabs sort and bucket on,
  // so a pill showing anything else contradicts the list the card sits in --
  // two "Upcoming" cards both stamped with the activation date read as
  // duplicates of a past day rather than as two different future lessons.
  // Falls back for callers with no schedule (the plain lessons list).
  const datePill = dayjs(
    lesson.scheduledFor || lesson.assignedAt || lesson.created_at,
  ).format("MMM D, YYYY");
  const isToday = lesson.finishedAt
    ? dayjs(lesson.finishedAt).isSame(dayjs(), "day")
    : false;
  const startedToday = lesson.startedAt
    ? dayjs(lesson.startedAt).isSame(dayjs(), "day")
    : false;

  const parsedMeta = (() => {
    const match = /^G(\d+)M(\d+)L(\d+)/i.exec(lesson.name || "");
    if (!match) return null;
    return { grade: match[1], mission: match[2], lnum: match[3] };
  })();

  const best = assessmentStatuses?.exit_ticket_best;
  const hasScore = best?.correct_count != null && best?.total_questions != null;
  const details = (
    <DetailsPopover label={lesson.name}>
      <dl>
        {!lesson.undated && (
          <>
            <dt>{lesson.scheduledFor ? "Scheduled for" : "Assigned"}</dt>
            <dd>{lesson.scheduledFor || lesson.assignedAt ? datePill : "—"}</dd>
            {!lesson.scheduledFor &&
              !lesson.assignedAt &&
              lesson.created_at && (
                <>
                  <dt>Created</dt>
                  <dd>{dayjs(lesson.created_at).format("MMM D, YYYY")}</dd>
                </>
              )}
          </>
        )}
        {lesson.status === "mastered" && (
          <>
            <dt>Finished</dt>
            <dd>
              {isToday
                ? "Today"
                : lesson.finishedAt
                  ? dayjs(lesson.finishedAt).format("MMM D, YYYY")
                  : "—"}
            </dd>
          </>
        )}
        {lesson.status === "in_progress" && lesson.startedAt != null && (
          <>
            <dt>Started</dt>
            <dd>
              {startedToday
                ? "Today"
                : dayjs(lesson.startedAt).format("MMM D, YYYY")}
            </dd>
          </>
        )}
        {parsedMeta && (
          <>
            <dt>Grade</dt>
            <dd>{parsedMeta.grade}</dd>
            <dt>Mission</dt>
            <dd>{parsedMeta.mission}</dd>
            <dt>Lesson</dt>
            <dd>{parsedMeta.lnum}</dd>
          </>
        )}
        {hasScore && (
          <>
            <dt>Best score</dt>
            <dd>
              {best.correct_count}/{best.total_questions} correct
            </dd>
            {best.submitted_at && (
              <>
                <dt>Achieved</dt>
                <dd>{dayjs(best.submitted_at).format("MMM D, YYYY")}</dd>
              </>
            )}
          </>
        )}
      </dl>
      {assessmentStatuses && (
        <AssessmentsDropdown statuses={assessmentStatuses} />
      )}
    </DetailsPopover>
  );
  const action = locked ? (
    <div className="et-lesson-action">
      <span className="et-locked">
        <Lock size={16} aria-hidden="true" />
        Locked
      </span>
    </div>
  ) : (
    <div className="et-lesson-action">
      <ActionButton
        onClick={() => startLesson(lesson)}
        canStart={canStart}
        unavailableLabel={unavailableLabel}
        unavailableReason={unavailableReason}
        disabled={
          !canStart ||
          isLoading ||
          (globalLoadingLessonId != null && globalLoadingLessonId !== lesson.id)
        }
        loading={isLoading || globalLoadingLessonId === lesson.id}
        status={lesson.status}
      />
      {details}
    </div>
  );
  return (
    <article
      className={`et-student-row${hasScore ? " has-score" : ""}`}
      data-status={lesson.status || "not_started"}
      data-available={availability ? String(availability.available) : undefined}
      aria-label={lesson.name}
    >
      <div className="et-goals">
        {totalGoals > 0 && (
          <>
            <div className="et-goals-visual">
              <CircularProgress
                value={goalsMet / totalGoals}
                size={48}
                thickness={3}
                color={borderColor}
                trackColor="#e8e3d6"
              />
              <b>
                {goalsMet}
                <span>/{totalGoals}</span>
              </b>
            </div>
            <span>goals</span>
            <span className="sr-only">
              {goalsMet} of {totalGoals} goals completed
            </span>
          </>
        )}
      </div>
      <div>
        <h3>{lesson.name}</h3>
        <div className="et-lesson-meta">
          {position ? (
            <span
              className="et-availability"
              data-tone={position.next ? "available" : undefined}
            >
              {position.label}
            </span>
          ) : (
            <span
              className="et-status"
              data-status={lesson.status || "not_started"}
            >
              {lesson.status === "mastered"
                ? "Completed"
                : lesson.status === "in_progress"
                  ? "In Progress"
                  : "Not Started"}
            </span>
          )}
          {availability && (
            <span
              className="et-availability"
              data-tone={
                availability.tone ??
                (availability.available ? "available" : "pending")
              }
              title={unavailableReason}
            >
              {availability.label}
            </span>
          )}
          {sequenceLabel && <span>{sequenceLabel}</span>}
        </div>
      </div>
      {hasScore && (
        <div className="et-best-score">
          <span>Best exit-ticket score</span>
          <strong>
            {best.correct_count}/{best.total_questions}{" "}
            <small className="text-xs font-normal">correct</small>
          </strong>
        </div>
      )}
      {action}
    </article>
  );
};

export default LessonCard;
